/**
 * The page a pilot advisor is held on, before onboarding or anything else, until they accept
 * their program's participation agreement. The wording comes from the program's entry in
 * `pilotPrograms.ts`; the server stores that same wording with each acceptance.
 *
 * An advisor whose program has no agreement is sent home, where the router decides what is next.
 */
import { Navigate } from 'react-router-dom';

import { Button, Card, CardContent, Checkbox, cn } from '@design-system';
import { useParticipationAgreement } from '@client/hooks/useParticipationAgreement';

export function AdvisorParticipationAgreement({ advisorId }: AdvisorIdProps) {
  const { agreement, hasChecked, setHasChecked, accept, isSubmitting } =
    useParticipationAgreement(advisorId);

  if (!agreement) {
    return (
      <Navigate
        to="/"
        replace
      />
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <div className="m-auto w-full max-w-2xl space-y-6 p-6">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-950">{agreement.title}</h2>
          <p className="text-foreground mt-2">
            Please review and accept the agreement to continue.
          </p>
        </div>

        <Card className="overflow-hidden p-0">
          <CardContent className="p-0">
            <div className="max-h-[60vh] overflow-y-auto">
              <p className="text-foreground whitespace-pre-line p-4 text-sm leading-relaxed">
                {agreement.text}
              </p>
            </div>
          </CardContent>
        </Card>

        <div className={cn('flex items-start gap-3', isSubmitting && 'opacity-60')}>
          <Checkbox
            id="participation-agreement-consent"
            checked={hasChecked}
            onCheckedChange={checked => setHasChecked(checked === true)}
            disabled={isSubmitting}
            className="mt-0.5"
          />
          <label
            htmlFor="participation-agreement-consent"
            className={cn(
              'text-foreground text-sm',
              isSubmitting ? 'cursor-not-allowed' : 'cursor-pointer',
            )}
          >
            {agreement.checkboxLabel}
          </label>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={accept}
            disabled={!hasChecked || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
}
